import { Radio } from 'antd';
import { useLayerProperitesContext } from '../../../../context/LayerPropertiesContext';
import { useEffect, useMemo, useRef, useState } from 'react';
import MapPortalService from '../../../../services/MapPortal/mapportal.service';
import MapIconsService from '../../../../services/MapIconService/mapIcon.service';
import { ALLOWED_ICON_MIME_TYPES, validateIconFile } from '../../../../services/MapIconService/mapicon.constants';
import { IconSelectorModal } from '../IconSelectorModal';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../../../enums/queryKeys';
import Notification from '../../../../shared/components/Notification';
import { NotificationTypes } from '../../../../enums/notificationTypes';
import { isBuiltInShape } from './components/builtInShapes';
import { useAuth } from '../../../../context/AuthContext';
import { LayerAction } from '../../../../enums/mapPortalActions';
import { layerActionAccess } from '../../constants';
import '../LayerProperties.scss';

// Sub-components
import SingleSymbology from './SingleSymbology';
import UniqueSymbology from './UniqueSymbology';
import QuantitySymbology from './QuantitySymbology';
import HeatmapSymbology from './HeatmapSymbology';

const Symbology = () => {
  const { uniqueLayerName, symbology, symbologyConfig, setState, selectedIcon, uniqueIcon, quantityIcon, layerType } = useLayerProperitesContext();
  // US-101677 Scenario 2: Heatmap is only available for point layers.
  const isPointLayer = layerType === 'Point' || layerType === 'MultiPoint';
  // An explicit `disabled` outranks ConfigProvider's componentDisabled default.
  const { canAccess } = useAuth();
  const canEditLayer = canAccess(layerActionAccess(LayerAction.EDIT_LAYER_PROPERTIES));

  // Field list comes from the layer SCHEMA (not a capped feature fetch), so the
  // dropdown is complete for layers of any size. Per-field values for Unique
  // (distinct) and Quantity (sample) are fetched on demand, server-side.
  const { getLayerSymbologyFields, getFieldDistinctValues, getFieldValueSample } = MapPortalService();
  const { data: fieldsResponse } = useQuery(getLayerSymbologyFields(uniqueLayerName, true));

  const uniqueField = symbologyConfig?.type === 'unique' ? symbologyConfig.field : undefined;
  const quantityField = symbologyConfig?.type === 'quantity' ? symbologyConfig.field : undefined;

  const { data: distinctResponse } = useQuery(getFieldDistinctValues(uniqueLayerName, symbology === 'unique' ? uniqueField : undefined));
  const { data: sampleResponse } = useQuery(getFieldValueSample(uniqueLayerName, symbology === 'quantity' ? quantityField : undefined));

  // Per-field value maps ({ [field]: values }) handed to the child tables — the
  // shape they already expect, just scoped to the selected field.
  const uniqueFeatures = useMemo(
    () => (uniqueField && distinctResponse?.values ? { [uniqueField]: distinctResponse.values } : {}),
    [uniqueField, distinctResponse],
  );
  const quantityFeatures = useMemo(
    () => (quantityField && sampleResponse?.values ? { [quantityField]: sampleResponse.values } : {}),
    [quantityField, sampleResponse],
  );
  const { getMapIcons, getPresignedUrl, uploadIcon, touchRecentIcon } = MapIconsService();
  const { refetch: refetchMapIcons } = useQuery({
    ...getMapIcons(),
    enabled: false,
  });
  const { mutateAsync: getPresignedUrlAsync } = useMutation(getPresignedUrl());
  const { mutateAsync: uploadIconAsync } = useMutation(uploadIcon());
  const { mutate: touchRecentIconMutate } = useMutation(touchRecentIcon());
  const queryClient = useQueryClient();

  // Data State
  const [keyOptions, setKeyOptions] = useState<{ label: string; value: string }[]>([]);
  const [quantityFieldOptions, setQuantityFieldOptions] = useState<{ label: string; value: string; disabled?: boolean }[]>([]);

  // Icon Modal State
  const [showIconSelector, setShowIconSelector] = useState(false);
  const [mapIcons, setMapIcons] = useState<any[]>([]);
  const [iconSelectionTarget, setIconSelectionTarget] = useState<'single' | 'unique' | 'quantity'>('single');

  // US-101664: a unique category edits its icon through a *promise-based*
  // request so the heavy "Choose from styles" / Upload plumbing (modal,
  // presign, S3 PUT) isn't duplicated per category. When a resolver is
  // pending, handleIconSelect / handleIconUpload resolve it instead of writing
  // the flat single/unique context fields.
  const pendingIconResolverRef = useRef<((result: { key: string } | null) => void) | null>(null);

  const requestExternalIcon = (source: 'styles' | 'upload'): Promise<{ key: string } | null> =>
    new Promise<{ key: string } | null>((resolve) => {
      pendingIconResolverRef.current = resolve;
      if (source === 'styles') {
        refetchMapIcons().then((response) => {
          if (response.data) setMapIcons(response.data);
          setShowIconSelector(true);
        });
      } else {
        document.getElementById('icon-upload-input')?.click();
      }
    });

  const resolvePendingIcon = (result: { key: string } | null): boolean => {
    const resolver = pendingIconResolverRef.current;
    if (!resolver) return false;
    pendingIconResolverRef.current = null;
    resolver(result);
    return true;
  };

  // Determine which icon is currently selected based on the active target
  const selectedIconKey = (() => {
    if (iconSelectionTarget === 'single') return selectedIcon;
    if (iconSelectionTarget === 'unique') return uniqueIcon;
    if (iconSelectionTarget === 'quantity') return quantityIcon;
    return undefined;
  })();

  // --- Field options from the layer SCHEMA (complete regardless of size) ---
  useEffect(() => {
    const fields: { name: string; numeric: boolean }[] = fieldsResponse?.fields ?? [];

    setKeyOptions(fields.map((f) => ({ label: f.name, value: f.name })));

    // US-101669 Scenario 2: show ALL fields; only numeric fields are selectable,
    // non-numeric ones are visible but disabled. `numeric` now comes from the
    // column type (information_schema), not by sniffing a sample value.
    setQuantityFieldOptions(fields.map((f) => ({ label: f.name, value: f.name, disabled: !f.numeric })));
  }, [fieldsResponse]);

  const iconFieldNameFor = (target: 'single' | 'unique' | 'quantity') =>
    target === 'single' ? 'selectedIcon' : target === 'unique' ? 'uniqueIcon' : 'quantityIcon';

  const iconTypeFieldNameFor = (target: 'single' | 'unique' | 'quantity') =>
    target === 'single' ? 'iconType' : target === 'unique' ? 'uniqueIconType' : 'quantityIconType';

  // --- Handlers ---
  const handleIconRequest = async (source: 'recent' | 'styles' | 'upload', target: 'single' | 'unique' | 'quantity') => {
    setIconSelectionTarget(target);

    if (source === 'styles') {
      const response = await refetchMapIcons();
      if (response.data) setMapIcons(response.data);
      setShowIconSelector(true);
    } else if (source === 'upload') {
      document.getElementById('icon-upload-input')?.click();
    }
    // "recent" is handled in-line by IconControls' popover; no modal needed.
  };

  const handleIconUpload = async (file: File) => {
    // US-101662 Scenario 4: validate BEFORE we hit the network so the user
    // sees the exact AC wording (and we never spend a presign round-trip on
    // a doomed upload).
    const validationError = validateIconFile(file);
    if (validationError) {
      Notification({ message: validationError, type: NotificationTypes.ERROR });
      return;
    }

    try {
      const data = await getPresignedUrlAsync({
        fileName: file.name,
        fileType: file.type,
      });
      if (!data) return;
      const { url, key } = data;

      // Direct S3 PUT — the backend never proxies the binary, so even a 2MB
      // upload doesn't tie up an API worker.
      const putResponse = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!putResponse.ok) {
        throw new Error(`S3 upload failed (${putResponse.status})`);
      }

      await uploadIconAsync({ key, name: file.name });

      // US-101664: if a category is awaiting this upload, hand it the key and
      // skip the flat-field write entirely.
      if (resolvePendingIcon({ key })) {
        touchRecentIconMutate({ key });
        queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_RECENT_ICONS] });
        return;
      }

      // Update the relevant icon field + mark as image so SingleSymbology
      // disables fill/outline (Scenario 3). Also push to "recently used".
      // iconType must be "upload" so the SLD generator emits an
      // ExternalGraphic (raster icon) for this entry — see notes in
      // IconControls.selectIcon.
      const field = iconFieldNameFor(iconSelectionTarget);
      const typeField = iconTypeFieldNameFor(iconSelectionTarget);
      setState((prev) => ({
        ...prev,
        [field]: key,
        [typeField]: 'upload',
        iconUploadKind: 'image',
      }));
      touchRecentIconMutate({ key });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_RECENT_ICONS] });
      setIconSelectionTarget('single');
    } catch (err) {
      console.error(err);
    }
  };

  const handleIconSelect = (iconKey: string) => {
    // US-101664: a pending category request takes precedence over the flat
    // single/unique field write.
    if (resolvePendingIcon({ key: iconKey })) {
      touchRecentIconMutate({ key: iconKey });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_RECENT_ICONS] });
      setShowIconSelector(false);
      return;
    }
    const field = iconFieldNameFor(iconSelectionTarget);
    const typeField = iconTypeFieldNameFor(iconSelectionTarget);
    // Built-in shapes (e.g. shape:circle) participate in full fill/outline
    // controls — anything else (an S3 key) is treated as an image.
    const kind = isBuiltInShape(iconKey) ? 'shape' : 'image';
    setState((prev) => ({
      ...prev,
      [field]: iconKey,
      // For "Choose from styles" the result is an S3 image -> ExternalGraphic
      // branch in the SLD. For built-in shapes (shouldn't usually arrive via
      // this modal but defensively) -> Mark branch.
      [typeField]: kind === 'shape' ? 'shape' : 'styles',
      iconUploadKind: kind,
    }));
    touchRecentIconMutate({ key: iconKey });
    queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_RECENT_ICONS] });
    setShowIconSelector(false);
  };

  return (
    <>
      <div className="lp-symbology__card lp-symbology__type-card">
        <div className="lp-symbology__card-header">SYMBOLOGY TYPE</div>
        <div className="lp-symbology__switch-row">
          <Radio.Group value={symbology} onChange={(e) => setState((prev) => ({ ...prev, symbology: e.target.value }))}>
            <Radio.Button value="single">Single</Radio.Button>
            <Radio.Button value="unique">Unique</Radio.Button>
            <Radio.Button value="quantity">Quantity</Radio.Button>
            <Radio.Button value="heatmap" disabled={!isPointLayer || !canEditLayer}>
              Heatmap
            </Radio.Button>
          </Radio.Group>
        </div>
      </div>
      <div className="lp-symbology__content">
        {symbology === 'single' && <SingleSymbology onRequestIconSelector={(source) => handleIconRequest(source, 'single')} />}

        {symbology === 'unique' && <UniqueSymbology keyOptions={keyOptions} features={uniqueFeatures} onRequestExternalIcon={requestExternalIcon} />}

        {symbology === 'quantity' && (
          <QuantitySymbology fieldOptions={quantityFieldOptions} features={quantityFeatures} onRequestExternalIcon={requestExternalIcon} />
        )}

        {symbology === 'heatmap' && <HeatmapSymbology />}

        {/* Hidden Upload Input. Restricting `accept` is a hint, not a
          guarantee — the validation above is the actual gate. */}
        <input
          id="icon-upload-input"
          type="file"
          accept={ALLOWED_ICON_MIME_TYPES.join(',')}
          className="lp-hidden"
          onChange={(e) => {
            if (e.target.files?.[0]) {
              handleIconUpload(e.target.files[0]);
              e.target.value = '';
            } else {
              // Upload dialog cancelled — release any waiting category request.
              resolvePendingIcon(null);
            }
          }}
        />

        {/* Icon Selector Modal */}
        <IconSelectorModal
          icons={mapIcons}
          open={showIconSelector}
          onClose={() => {
            setShowIconSelector(false);
            setIconSelectionTarget('single');
            // Releasing a pending category request resolves it as "cancelled".
            resolvePendingIcon(null);
          }}
          onSelect={handleIconSelect}
          selectedIconKey={selectedIconKey}
        />
      </div>
    </>
  );
};

export default Symbology;
